import React, { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { explodingObject } from '@util/contextHelpers';
import { trackPromise } from '@util/promise';

export type SheetLoadingState = {
    isLoading: boolean;
    isError: boolean;
    logs: LoadingLog[];
    progress: number;
};

export type LoadingLog = {
    message: string;
    time: Date;
    error?: boolean;
    elapsedTime?: number;
};

export type SheetDataLoadingContextType = {
    state: SheetLoadingState;
    api: {
        startLoading: () => void;
        endLoading: () => void;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        trackLoading: <T = any>(p: Promise<T>) => Promise<T>;
        setProgress: (progress: number) => void;

        pushMessage: (message: string, elapsedTime?: number, progress?: number) => void;
        pushError: (error: string) => void;

        clearErrors: () => void;
    };
};

const LOADING_FLASH = 50;

export const SheetDataLoadingContext = createContext<SheetDataLoadingContextType>(
    explodingObject('SheetDataLoadingContext used without being provided')
);

export const useSheetDataLoadingContext = () => useContext(SheetDataLoadingContext);

export const SheetDataLoadingProvider = ({ children }: PropsWithChildren) => {
    // Count number of sheets currently loading
    const [loadingCount, setLoadingCount] = useState<number>(0);

    // Count number of undismissed errors
    const [errorCount, setErrorCount] = useState<number>(0);

    // Collect message/error log
    const [logs, setLogs] = useState<LoadingLog[]>([]);

    // Global progress indicator
    const [progress, setProgress] = useState<number>(0);

    const state = useMemo(
        () => ({
            isLoading: !!loadingCount,
            isError: !!errorCount,
            logs,
            progress,
        }),
        [loadingCount, errorCount, logs, progress]
    );

    const api = useMemo(() => {
        const startLoading = () => {
            setErrorCount(0);
            setLoadingCount((c) => c + 1);
        };
        const endLoading = () => {
            // Delay the end decrement so that if a new job starts immediately in an effect,
            // the indicator doesn't flash off/on
            setTimeout(() => setLoadingCount((c) => c - 1), LOADING_FLASH);
        };
        const trackLoading = trackPromise(startLoading, endLoading);

        const pushLog = (log: LoadingLog) => setLogs((ls) => [...ls, log]);

        const pushMessage = (message: string, elapsedTime?: number) => {
            pushLog({
                time: new Date(),
                message,
                elapsedTime,
            });
        };

        const pushError = (message: string) => {
            pushLog({
                time: new Date(),
                message,
                error: true,
            });
            setErrorCount((c) => c + 1);
        };

        const clearErrors = () => setErrorCount(0);

        return { startLoading, endLoading, trackLoading, setProgress, pushMessage, pushError, clearErrors };
    }, []);

    const context = useMemo(() => ({ state, api }), [state, api]);

    return <SheetDataLoadingContext.Provider value={context}>{children}</SheetDataLoadingContext.Provider>;
};
